import React, { useContext, useMemo, useState } from 'react';
import { Card } from "tea-component";
import moment from 'moment';
import { getLocale, useIntl, useSelector } from 'umi';
import MetricCardItem from '.';
import MetricCardChartItem from './MetricCardChartItem';
import MetricCardTitle from './MetricCardTitle';
import { formatSeries, formatSeriesInflextion, getDecimalFormateStr } from '../MetricCard/utils';
import { getExplainVal } from '../../utils/getExplainVal';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import "@/pages/dashboard/assets/scss/page/Overlay/components/cartContext.scss";
import './index.scss';
import { jumpDetail } from '../../utils/utils';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { resolveMainValueDisplay } from '../../utils/dataFormatter';
import ACard from '../ACard';
import VsBenchmarkBtn from './components/VsBenchmarkBtn';
import classNames from 'classnames';
import { JumpType, navigate } from '@home/utils/utils';
import { dynamicString } from '@home/utils/dynamicString';
import { getLanguage, getName } from '@home/utils/common';

export default function MetricCard(props: any) {
  const { config, data, metricsData, trendData, xAxisData, isChange, isRealTime, isNightingale, isPlaceholderTime, gameCode: propsGameCode, benchmarkConfig, hasVs: propsHasVs, ...restProps } = props;
  const { mainMetricsList = [], isDark = false, haveJump = false, isMoblie = false } = data || {};
  const { componentMetrics = [], jumpUrl, jumpType, isShowIcon = true, index, hasVs, className, style, haveTrendChart = true, gradientStartColor, gradientEndColor, isRightCard = false, isLeftCard = false,
    subMetricConfig
  } = config;
  const { formatMessage } = useIntl();
  const contextObj = useContext(FilterContext);
  const { filterVal } = contextObj || {};
  const { timeType = 'day', singleDaily, contrastDaily, rangeDateKey } = filterVal || {};
  const { explainInfo, gameCode } = useSelector((state: any) => ({
    explainInfo: state.dashboard?.explainInfo,
    gameCode: propsGameCode || state?.dashboard?.currentGame?.game_code,
  }));
  const [isClick, setIsClick] = useState(false);
  const isEN = getLocale() === 'en-US';

  const isSingleToday = useMemo(() => {
    const singleDailyDate = moment(singleDaily);
    return singleDailyDate.isSame(moment(), 'day'); // 检查 singleDaily 是否为今天
  }, [singleDaily]);

  const isContrastSevenDaysAgo = useMemo(() => {
    const contrastDailyDate = moment(contrastDaily);
    const sevenDaysAgo = moment().subtract(7, 'days');
    return contrastDailyDate.isSame(sevenDaysAgo, 'day'); // 检查 contrastDaily 是否为今天减去 7 天
  }, [contrastDaily]);

  const isContrastSDLW = isSingleToday && isContrastSevenDaysAgo;

  const dataSource = useMemo(() => {
    let newData: any = {};
    let realtimeCardData: any = {};
    let newNightingaleData: any = {};

    // 处理 subMetricConfig
    const processSubMetric = () => {
      if (!subMetricConfig) return null;
      
      const {
        metricKey: subMetricKey,
        enName: subEnName,
        cnName: subCnName,
        explainEn: subEnExplain,
        explainCn: subCnExplain,
        formatRule: subFormatter,
      } = subMetricConfig;
      
      const subDataItem = metricsData?.find((v: { metrics_name: string; }) => v?.metrics_name === subMetricKey) || {};
      const { value: subValue } = subDataItem;
      
      const subName = getName(isEN, dynamicString(subEnName, data), dynamicString(subCnName, data)) || '';
      const formattedSubValue = subFormatter 
        ? dataFormat({ value: subValue, formatRule: dynamicString(subFormatter, data) })
        : subValue;
      const subExplain = getName(isEN, dynamicString(subEnExplain, data), dynamicString(subCnExplain, data)) || '';
      
      return {
        name: subName,
        value: formattedSubValue,
        explain: subExplain
      };
    };

    const subMetric = processSubMetric();

    componentMetrics?.map((item: any) => {
      const {
        trendKey,
        metricKey,
        enName,
        cnName,
        rangeEnName,
        rangeCnName,
        preEnName,
        preCnName,
        endEnName,
        endCnName,
        rainbowKey,
        explainEn,
        explainCn,
        type,
        formatRule: formatter,
        mainValueFormatRule,
        decimal,
        icon,
        haveLineChart,
      } = item || {};
      const dataItem = metricsData?.find((v: { metrics_name: string; }) => v?.metrics_name === metricKey) || {};
      const { metrics_name, value, componentData, ...other } = dataItem;
      let name = getName(isEN, dynamicString(enName, data), dynamicString(cnName, data)) || '';
      // 多天名称
      const rangeName = getName(isEN, dynamicString(rangeEnName, data), dynamicString(rangeCnName, data)) || '';
      const preName = getName(isEN, dynamicString(preEnName, data), dynamicString(preCnName, data)) || '';
      const endName = getName(isEN, dynamicString(endEnName, data), dynamicString(endCnName, data)) || '';
      if (isChange) {
        name = rangeName || name;
      }
      const explain = getLanguage(isEN, dynamicString(explainEn, data), dynamicString(explainCn, data)) || '';
      // 兼容老的解释, 后续配置更新完可删除
      const explainOld = getExplainVal(explainInfo, rainbowKey, getLocale(), explainEn, explainCn);
      const resolvedFormatter = dynamicString(formatter, data);
      const baseFormattedValue = formatter ? dataFormat({ value, formatRule: resolvedFormatter }) : getDecimalFormateStr(type, value, metrics_name, decimal);
      const { mainDisplayValue, mainValueBubble } = resolveMainValueDisplay({
        value,
        data,
        baseFormattedValue,
        mainValueFormatRule,
      });

      const newItem = {
        ...item,
        name,
        preName,
        endName,
        icon,
        metrics_name,
        value: mainDisplayValue,
        mainValueBubble,
        bubbleData: { ...other },
        explain: (explain || explainOld)?.replaceAll('\\n', '<br/>'),
        formatRule: resolvedFormatter,
        subMetric,
      };

      if (mainMetricsList?.includes(metricKey) || mainMetricsList?.includes(trendKey)) {
        const series = formatSeries(trendData?.[trendKey || metrics_name || metricKey] || [], timeType?.toUpperCase(), formatMessage);
        // 第一个主指标占主位；副指标仅 haveLineChart 时带趋势
        if (!Array.isArray(newData?.list)) {
          newData = { ...newItem, xAxisData, series, list: [] };
          return;
        }
        newData.list.push(haveLineChart ? { ...newItem, xAxisData, series } : newItem);
        return;
      }
      newData?.list?.push(newItem);

      // 夜莺卡片趋势图
      const series = formatSeriesInflextion(trendData?.[trendKey ? trendKey : metrics_name] || [], timeType?.toUpperCase(), formatMessage, isContrastSDLW);
      newNightingaleData = { ...newItem, xAxisData, series, list: [] };


      // newNightingaleData?.list?.push(newItem)
      realtimeCardData = { ...newItem };
    });
    return (isRealTime && isNightingale) ? newNightingaleData : (isRealTime ? realtimeCardData : newData || {});
  }, [componentMetrics, mainMetricsList, xAxisData, timeType, trendData, metricsData, isEN, subMetricConfig, data]);


  const jumptoDetail = () => {
    if (!jumpUrl || jumpUrl === "none") return;
    setIsClick(true);

    const { diff, timeType, ...filters } = filterVal || {};
    const newFilterVal = {
      diff,
      timeType,
      rangeDateKey,
      filterValue: {
        ...filters
      }
    };

    const resolvedUrl = `/dashboard/game/${gameCode}${jumpUrl ? jumpUrl : '/overview/daily'}`;

    if (jumpType === JumpType.Window) {
      navigate(resolvedUrl, { jumpType });
      return;
    }

    jumpDetail(resolvedUrl, newFilterVal);
  };

  const cardStyle: React.CSSProperties = {
    ...style,
    ...(gradientStartColor || gradientEndColor ? {
      '--gradient-start-color': gradientStartColor,
      '--gradient-end-color': gradientEndColor,
    } : {})
  };

  return (
    <ACard url={jumpUrl && jumpUrl !== "none" ? `/dashboard/game/${gameCode}${jumpUrl}` : ''}>
      <div onClick={() => haveJump && jumptoDetail()}>
        <Card 
          className={classNames("dbd-overlay-card overlay-card", className)} 
          style={cardStyle}
        >
          <Card.Body
            className={isClick && (jumpType !== JumpType.Window) ? 'overlay-card-click' : ''}
            title={
              <div className={classNames({
                'dbd-overlay-card-container': !isNightingale,
              })}>
                <MetricCardTitle
                  dataSource={dataSource}
                  isDark={isDark}
                  jumpUrl={jumpUrl}
                  isRealTime={isRealTime}
                  isShowIcon={isShowIcon}
                  isNightingale={isNightingale} />
                <VsBenchmarkBtn hasVs={propsHasVs && hasVs} benchmarkConfig={benchmarkConfig} componentMetric={componentMetrics?.[0]} {...restProps} />
              </div>
            }
          >
            <div className="overlay-card-flex">
              <MetricCardItem
                isMain
                isMoblie={isMoblie}
                isChange={isChange}
                dataSource={dataSource}
                timeType={timeType}
                isRealTime={isRealTime}
                isNightingale={isNightingale}
                isPlaceholderTime={isPlaceholderTime}
                isRightCard={isRightCard}
              />
              {((isRealTime && isNightingale) || (!isRealTime && haveTrendChart)) &&
                <MetricCardChartItem
                  isMoblie={isMoblie}
                  dataSource={dataSource}
                  timeType={timeType}
                  isNightingale={isNightingale}
                  filterVal={filterVal}
                  isContrastSDLW={isContrastSDLW}
                  index={index}
                  isLeftCard={isLeftCard}
                />}
            </div>
            {dataSource?.list?.length ?
              <div className="overlay-card-flex margin-t-30">
                {
                  dataSource?.list?.map((item: any) => (
                    <React.Fragment key={item?.metrics_name}>
                      <MetricCardItem
                        isMoblie={isMoblie}
                        isChange={isChange}
                        dataSource={item}
                        timeType={timeType}
                        isRealTime={isRealTime}
                        isNightingale={isNightingale}
                      />
                      {!isRealTime && haveTrendChart && item?.haveLineChart && item?.series && (
                        <MetricCardChartItem
                          isMoblie={isMoblie}
                          dataSource={item}
                          timeType={timeType}
                          isNightingale={isNightingale}
                          filterVal={filterVal}
                          isContrastSDLW={isContrastSDLW}
                          index={index}
                          isLeftCard={isLeftCard}
                        />
                      )}
                    </React.Fragment>
                  ))
                }
              </div> : null
            }
          </Card.Body>
        </Card>
      </div>
    </ACard>
  );
};